'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';
import Script from 'next/script';

type BellmetricWindow = Window &
  typeof globalThis & {
    _bellmetric_privacy_enabled?: boolean;
    _bellmetric?: unknown[];
    CookieInformation?: {
      getConsentGivenFor?: (cat: string) => boolean;
    };
  };

/**
 * Bellmetric (bmetric) integration following the official install guide.
 *
 * Engagement Engine (overlay/modal): bm.js attaches on its own - no host div
 * needed on our side. `bmnetContainer` is Bellmetric’s class on their overlay;
 * we do not render it.
 *
 * Render-to-target forms (CFB/RTT): need a specific id/class on the page to
 * inject into - see ActionFormBlock `bmLeadformContainer` on `/ring-mig-op`.
 *
 * eesy.dk and www.eesy.dk use the production Bellmetric id passed from
 * `BELLMETRIC_SCRIPT_ID`. Any other hostname uses the development site id.
 * Bellmetric has approved that id for thankful-forest-09e49c403.7.azurestaticapps.net.
 *
 * 1. Set privacy flag on mount via onConsent() (bm.js is async, so this runs first).
 * 2. Load bm.js on all pages.
 * 3. Push virtual pageview on SPA navigation.
 * 4. Update privacy flag when CookieInformation consent changes.
 */
interface BellmetricScriptProps {
  bellmetricId?: string;
}

const DEVELOPMENT_BELLMETRIC_ID = '937dbe71-24cc-407f-ad9b-ed385e7d8080';
const EESY_HOSTNAMES = new Set(['eesy.dk', 'www.eesy.dk']);

function subscribeToHostnameChange() {
  return () => {};
}

function getHostnameSnapshot() {
  return window.location.hostname.toLowerCase();
}

function getServerHostnameSnapshot() {
  return undefined;
}

export default function BellmetricScript({ bellmetricId }: BellmetricScriptProps) {
  const pathname = usePathname();
  const isFirstLoad = useRef(true);
  const hostname = useSyncExternalStore(
    subscribeToHostnameChange,
    getHostnameSnapshot,
    getServerHostnameSnapshot
  );
  const activeBellmetricId = hostname
    ? (EESY_HOSTNAMES.has(hostname) ? bellmetricId : DEVELOPMENT_BELLMETRIC_ID)
    : undefined;

  // Update privacy flag when cookie consent changes
  useEffect(() => {
    if (!activeBellmetricId) return;

    const w = window as BellmetricWindow;

    function onConsent() {
      const ci = w.CookieInformation;
      const hasConsent =
        ci &&
        typeof ci.getConsentGivenFor === 'function' &&
        ci.getConsentGivenFor('cookie_cat_functional');
      w._bellmetric_privacy_enabled = !hasConsent;

      // Refresh after consent change (per doc: "Updating consent without new page load")
      w._bellmetric = w._bellmetric || [];
      w._bellmetric.push(['pageview', window.location.href]);
    }

    // Returning visitors with stored consent: uc.js fires `CookieInformationConsentGiven`
    // very early — often before this effect mounts — so the listener alone would miss it
    // and leave the flag at its inline default. Sync once on mount.
    onConsent();
    window.addEventListener('CookieInformationConsentGiven', onConsent);
    return () => window.removeEventListener('CookieInformationConsentGiven', onConsent);
  }, [activeBellmetricId]);

  // SPA: push virtual pageview on each client-side navigation
  useEffect(() => {
    if (!activeBellmetricId) return;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    const w = window as BellmetricWindow;
    w._bellmetric = w._bellmetric || [];
    w._bellmetric.push(['pageview', window.location.href]);
  }, [pathname, activeBellmetricId]);

  if (!activeBellmetricId) return null;

  return (
    <>
      {/* Load bmetric main script on all pages (overlay + RTT where configured).
          The privacy flag is set by `onConsent()` in the effect above, which runs
          on mount (privacy ON unless functional consent is given) and again on
          every CookieInformationConsentGiven event. bm.js still loads without
          consent (so RTT forms like /ring-mig-op render) but does not track.

          NOTE: do not add an inline `<Script strategy="beforeInteractive">` here.
          This component only renders after hydration (hostname is unknown on the
          server), and React never executes <script> tags created client-side — it
          only logs "Encountered a script tag while rendering React component". */}
      <Script
        id="jsBmetric001"
        src={`//web.telemetric.dk/t/${activeBellmetricId}/bm.js`}
        strategy="afterInteractive"
        async
      />
    </>
  );
}
